import type { LatLngTuple } from 'leaflet';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { GPS_COORDINATE_DECIMALS } from '../constants/instituteSubmission.constants';

function formatGpsPoint([latitude, longitude]: LatLngTuple): string {
  return `${latitude.toFixed(GPS_COORDINATE_DECIMALS)}, ${longitude.toFixed(GPS_COORDINATE_DECIMALS)}`;
}

function copyWithHiddenTextArea(text: string): boolean {
  const previouslyFocused = document.activeElement;
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.readOnly = true;
  textArea.className = 'fixed top-0 left-0 opacity-0';
  document.body.append(textArea);
  textArea.select();
  const isCopied = document.execCommand('copy');
  textArea.remove();
  if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
  return isCopied;
}

async function copyText(text: string): Promise<boolean> {
  if (!window.isSecureContext) return copyWithHiddenTextArea(text);

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

interface CopyableGpsPointProps {
  position: LatLngTuple;
}

export function CopyableGpsPoint({ position }: CopyableGpsPointProps) {
  const gpsPointText = formatGpsPoint(position);

  async function handleCopy() {
    const isCopied = await copyText(gpsPointText);
    if (!isCopied) {
      toast.error('Could not copy the GPS point');
      return;
    }
    toast.success('GPS point copied');
  }

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      title="Copy GPS point"
      aria-label={`Copy GPS point ${gpsPointText}`}
      className="-mr-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 font-medium tabular-nums transition-colors hover:bg-muted max-md:min-h-10"
    >
      {gpsPointText}
      <Copy className="size-3 text-muted-foreground" aria-hidden />
    </button>
  );
}
